"use client";
import { Room } from "livekit-client";
import { useLocale, useTranslations } from "next-intl";
import { useAppSelector } from "@client/store/store.hooks";
import MeetContent from "@client/components/meet/MeetContent";
import type { LocalUserChoices } from "@livekit/components-react";
import type { ConnectionDetails } from "@client/types/connection.types";
import type { JoinRequestWithTimer } from "@client/components/meet/hooks";
import { MuteNotification } from "@client/components/ui/MuteNotification";
import { RoomContext, RoomAudioRenderer } from "@livekit/components-react";
import { Logger } from "@shared/utils/logger";
import { X } from "lucide-react";
import { JSX, useCallback, useState, useMemo, memo, useEffect, useRef } from "react";
import { useOptionalCallSocket } from "@client/ctx/CallSocketProvider";
import { isMeetChatEnabled, resolveMeetChatId, resolveChatEmbedUrl } from "@client/utils/chatIframe.utils";
import { mirrorGuestChatActivity } from "@client/utils/guestChatMirror.client";
import {
  useChat,
  usePolls,
  useNotes,
  useUIState,
  useReactions,
  useRecording,
  useWhiteboard,
  useMeetHandlers,
  useJoinRequests,
  useMediaControls,
  useRoomConnection,
  useMeetingEndState,
  useParticipantsList,
  useDataChannelManager,
  useParticipantMuteState,
  useMeetEventSounds
} from "@client/components/meet/hooks";
import { useSessionArtifactArchive } from "@client/components/meet/hooks/useSessionArtifactArchive";
import { BreakoutRoomsPanel } from "@client/components/meet/BreakoutRoomsPanel";
import { getBreakoutTheme } from "@client/utils/breakoutThemes";

/** Props for the Meet component configuration */
interface MeetProps {
  room: Room;
  userChoices: LocalUserChoices;
  isHost?: boolean;
  connectionDetails: ConnectionDetails;
  startPresenting?: boolean;
  onIntentionalLeave?: () => void;
  onBreakoutConnectionChange?: (details: {
    roomName: string;
    participantToken: string;
    chatId?: string | null;
    chatEnabled?: boolean;
    chatEmbedUrl?: string | null;
    breakoutSessionId?: string;
    breakoutRoomId?: string;
    breakoutTitle?: string;
    guestToken?: string | null;
    isGuest?: boolean;
    guestId?: string;
  }) => void;
}

/** Meet - Main Meeting Logic Controller. */
const Meet = memo(function Meet(props: MeetProps): JSX.Element {
  const { room, userChoices, startPresenting, onIntentionalLeave, onBreakoutConnectionChange } = props;

  // Get connection details from Redux
  const connectionDetails = useAppSelector((state) => state.connection.connectionDetails);
  const isHost = connectionDetails?.isHost ?? false;
  const canModerate = connectionDetails?.canModerate ?? isHost;
  const isModerator = connectionDetails?.isModerator === true || canModerate;
  const { chatId: socketChatId } = useOptionalCallSocket();
  const locale = useLocale();
  const tBreakout = useTranslations("breakoutRooms");

  const chatIframeEnabled = isMeetChatEnabled(connectionDetails, socketChatId);
  const chatIframeId = resolveMeetChatId(connectionDetails, socketChatId);
  const isGuest = connectionDetails?.isGuest === true;
  const guestId = connectionDetails?.guestId;
  const roomName = connectionDetails?.roomName;
  const [isBreakoutPanelOpen, setIsBreakoutPanelOpen] = useState(false);
  const [liveCursorsDisabled, setLiveCursorsDisabled] = useState(false);
  const [breakoutInvite, setBreakoutInvite] = useState<{
    breakoutRoomId: string;
    breakoutTitle: string;
    guestId?: string;
  } | null>(null);
  const [breakoutChoices, setBreakoutChoices] = useState<{
    rooms: Array<{ breakoutRoomId: string; name: string; participantCount: number }>;
  } | null>(null);
  const [breakoutCountdown, setBreakoutCountdown] = useState<number | null>(null);
  const [breakoutSessionEndsAt, setBreakoutSessionEndsAt] = useState<string | null>(null);
  const [breakoutTimerTick, setBreakoutTimerTick] = useState(() => Date.now());
  const [breakoutExpiredPromptOpen, setBreakoutExpiredPromptOpen] = useState(false);
  type BreakoutNotice =
    | { kind: "broadcast"; message: string; senderName: string; senderAvatarUrl?: string | null }
    | { kind: "status"; text: string };
  const [breakoutNotice, setBreakoutNotice] = useState<BreakoutNotice | null>(null);
  const [breakoutJoinError, setBreakoutJoinError] = useState<{
    message: string;
    breakoutRoomId: string;
  } | null>(null);
  const [pendingBreakoutAssignment, setPendingBreakoutAssignment] = useState<{
    breakoutRoomId: string;
    breakoutTitle: string;
    breakoutThemeColor: string;
  } | null>(null);
  const breakoutAssignmentPollTimerRef = useRef<number | null>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const breakoutToastTimeoutRef = useRef<number | null>(null);
  const BREAKOUT_BROADCAST_VISIBLE_MS = 30_000;
  const BREAKOUT_STATUS_VISIBLE_MS = 8_000;

  const showBreakoutNotice = useCallback((notice: BreakoutNotice) => {
    if (breakoutToastTimeoutRef.current) clearTimeout(breakoutToastTimeoutRef.current);
    setBreakoutNotice(notice);
    const durationMs = notice.kind === "broadcast" ? BREAKOUT_BROADCAST_VISIBLE_MS : BREAKOUT_STATUS_VISIBLE_MS;
    breakoutToastTimeoutRef.current = window.setTimeout(() => setBreakoutNotice(null), durationMs);
  }, []);
  // Guests can still use the in-room LiveKit chat if the event chat embed is
  // unavailable (for example, when guest event-chat provisioning fails).
  const inBreakoutRoom = Boolean(connectionDetails?.breakoutRoomId);
  const showChatButton = chatIframeEnabled || isGuest || inBreakoutRoom;

  const applyBreakoutConnection = useCallback(
    (data: {
      roomName: string;
      participantToken: string;
      chatId?: string | null;
      chatEnabled?: boolean;
      chatEmbedUrl?: string | null;
      breakoutSessionId?: string;
      breakoutRoomId?: string;
      breakoutTitle?: string;
    }) => onBreakoutConnectionChange?.(data),
    [onBreakoutConnectionChange]
  );

  const returnToMainMeeting = useCallback(async () => {
    if (!roomName) return;
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    try {
      const response = await fetch(`/api/meeting/${encodeURIComponent(roomName)}/breakouts/leave`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(connectionDetails?.guestId ? { guestId: connectionDetails.guestId } : {})
      });
      const result = (await response.json()) as {
        status: boolean;
        message?: string;
        data?: Parameters<typeof applyBreakoutConnection>[0];
      };
      if (!response.ok || !result.status || !result.data)
        throw new Error(result.message || "Could not return to the main meeting");
      applyBreakoutConnection(result.data);
      setBreakoutInvite(null);
      setBreakoutCountdown(null);
      setBreakoutChoices(null);
      setBreakoutJoinError(null);
      setBreakoutSessionEndsAt(null);
      setBreakoutExpiredPromptOpen(false);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Could not return to the main meeting";
      setBreakoutJoinError({ message, breakoutRoomId: connectionDetails?.breakoutRoomId ?? "" });
      Logger.error("BreakoutRooms", "Failed to return to the main meeting", error);
    }
  }, [applyBreakoutConnection, connectionDetails?.guestId, connectionDetails?.breakoutRoomId, roomName]);

  const joinBreakoutRoom = useCallback(
    async (breakoutRoomId: string, inviteGuestId?: string) => {
      if (!roomName) return;
      setBreakoutJoinError(null);
      try {
        const response = await fetch(`/api/meeting/${encodeURIComponent(roomName)}/breakouts/join`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            breakoutRoomId,
            guestId: inviteGuestId ?? (connectionDetails?.isGuest ? connectionDetails.guestId : undefined)
          })
        });
        const result = (await response.json()) as {
          status: boolean;
          message?: string;
          data?: Parameters<typeof applyBreakoutConnection>[0];
        };
        if (!response.ok || !result.status || !result.data)
          throw new Error(result.message || "Could not join the breakout room");
        applyBreakoutConnection(result.data);
        setBreakoutInvite(null);
        setBreakoutChoices(null);
        setPendingBreakoutAssignment(null);
        setIsBreakoutPanelOpen(false);
      } catch (error) {
        const message = error instanceof Error ? error.message : "Could not join the breakout room";
        setBreakoutJoinError({ message, breakoutRoomId });
        Logger.error("BreakoutRooms", "Failed to join breakout room", error);
      }
    },
    [applyBreakoutConnection, connectionDetails?.guestId, connectionDetails?.isGuest, roomName]
  );

  const refreshPendingBreakoutAssignment = useCallback(async (): Promise<boolean> => {
    if (!roomName || connectionDetails?.breakoutRoomId || breakoutChoices) {
      setPendingBreakoutAssignment(null);
      return false;
    }
    try {
      const query = connectionDetails?.guestId ? `?guestId=${encodeURIComponent(connectionDetails.guestId)}` : "";
      const response = await fetch(`/api/meeting/${encodeURIComponent(roomName)}/breakouts/assignment${query}`, {
        cache: "no-store"
      });
      const result = (await response.json()) as {
        status: boolean;
        data?: {
          breakoutRoomId: string;
          breakoutTitle: string;
          breakoutThemeColor: string;
        } | null;
      };
      if (response.ok && result.status && result.data) {
        setPendingBreakoutAssignment({
          breakoutRoomId: result.data.breakoutRoomId,
          breakoutTitle: result.data.breakoutTitle,
          breakoutThemeColor: result.data.breakoutThemeColor
        });
        return true;
      } else {
        setPendingBreakoutAssignment(null);
      }
    } catch {
      /* polling should not interrupt the meeting */
    }
    return false;
  }, [roomName, connectionDetails?.breakoutRoomId, connectionDetails?.guestId, breakoutChoices]);

  useEffect(() => {
    if (!roomName || connectionDetails?.breakoutRoomId) {
      setPendingBreakoutAssignment(null);
      if (breakoutAssignmentPollTimerRef.current !== null) {
        window.clearTimeout(breakoutAssignmentPollTimerRef.current);
        breakoutAssignmentPollTimerRef.current = null;
      }
      return;
    }

    let cancelled = false;
    let delayMs = 8_000;
    const poll = async () => {
      const assigned = await refreshPendingBreakoutAssignment();
      if (cancelled) return;

      // Data-channel breakout signals are the fast path. Polling remains only
      // as a fallback, backing off when no assignment is waiting.
      delayMs = assigned ? 8_000 : Math.min(delayMs * 2, 30_000);
      breakoutAssignmentPollTimerRef.current = window.setTimeout(poll, delayMs);
    };

    void poll();
    return () => {
      cancelled = true;
      if (breakoutAssignmentPollTimerRef.current !== null) {
        window.clearTimeout(breakoutAssignmentPollTimerRef.current);
        breakoutAssignmentPollTimerRef.current = null;
      }
    };
  }, [roomName, connectionDetails?.breakoutRoomId, refreshPendingBreakoutAssignment]);

  useEffect(() => {
    if (!breakoutInvite || connectionDetails?.breakoutRoomId) return;
    const theme = getBreakoutTheme(breakoutInvite.breakoutRoomId);
    setPendingBreakoutAssignment({
      breakoutRoomId: breakoutInvite.breakoutRoomId,
      breakoutTitle: breakoutInvite.breakoutTitle,
      breakoutThemeColor: theme.color
    });
  }, [breakoutInvite, connectionDetails?.breakoutRoomId]);

  const handleBreakoutDataReceived = useCallback(
    (signal: Record<string, unknown>) => {
      const type = typeof signal.type === "string" ? signal.type : "";
      const breakoutRoomId = typeof signal.breakoutRoomId === "string" ? signal.breakoutRoomId : undefined;
      const guestId = typeof signal.guestId === "string" ? signal.guestId : undefined;

      if (type === "breakout-auto-move" && breakoutRoomId) {
        void joinBreakoutRoom(breakoutRoomId, guestId);
      }
      if (type === "breakout-invite" && breakoutRoomId) {
        setBreakoutInvite({
          breakoutRoomId,
          breakoutTitle:
            (typeof signal.breakoutTitle === "string" ? signal.breakoutTitle : undefined) ||
            tBreakout("assignedRoomFallback"),
          guestId
        });
      }
      if (type === "breakout-move" && breakoutRoomId) {
        void joinBreakoutRoom(breakoutRoomId, connectionDetails?.guestId);
      }
      if (type === "breakout-return-main") {
        void returnToMainMeeting();
      }
      if (type === "breakout-unassign") {
        if (guestId && connectionDetails?.guestId && guestId !== connectionDetails.guestId) {
          return;
        }
        setBreakoutInvite(null);
        setPendingBreakoutAssignment(null);
        if (connectionDetails?.breakoutRoomId) {
          void returnToMainMeeting();
        }
      }
      if ((type === "breakout-self-select" || type === "breakout-room-counts") && Array.isArray(signal.rooms)) {
        const incoming = signal.rooms as Array<{
          breakoutRoomId: string;
          name?: string;
          participantCount: number;
        }>;
        setBreakoutChoices((current) => {
          const nameById = new Map((current?.rooms ?? []).map((room) => [room.breakoutRoomId, room.name] as const));
          return {
            rooms: incoming.map((room, index) => ({
              breakoutRoomId: room.breakoutRoomId,
              participantCount: room.participantCount,
              name:
                (typeof room.name === "string" && room.name.trim()) ||
                nameById.get(room.breakoutRoomId) ||
                tBreakout("breakoutRoomFallback", { number: index + 1 })
            }))
          };
        });
      }
      if (type === "breakout-broadcast-message" && typeof signal.message === "string" && signal.message) {
        showBreakoutNotice({
          kind: "broadcast",
          message: signal.message,
          senderName:
            (typeof signal.senderName === "string" && signal.senderName.trim()) || tBreakout("broadcastHostFallback"),
          senderAvatarUrl:
            typeof signal.senderAvatarUrl === "string" && signal.senderAvatarUrl.trim()
              ? signal.senderAvatarUrl.trim()
              : null
        });
      }
      if (type === "breakout-broadcast-voice") {
        if (signal.active) {
          const hostName = typeof signal.hostName === "string" ? signal.hostName : undefined;
          const label = hostName
            ? tBreakout("broadcastVoiceActive", { host: hostName })
            : tBreakout("broadcastVoiceActiveHost");
          showBreakoutNotice({ kind: "status", text: label });
        } else {
          setBreakoutNotice(null);
        }
      }
      if (type === "breakout-broadcast-share") {
        if (signal.active) {
          const hostName = typeof signal.hostName === "string" ? signal.hostName : undefined;
          const label = hostName
            ? tBreakout("broadcastShareActive", { host: hostName })
            : tBreakout("broadcastShareActiveHost");
          showBreakoutNotice({ kind: "status", text: label });
        } else {
          setBreakoutNotice(null);
        }
      }
      if (type === "breakout-session-timer") {
        setBreakoutSessionEndsAt(typeof signal.sessionEndsAt === "string" ? signal.sessionEndsAt : null);
      }
      if (type === "breakout-close-countdown") {
        if (!connectionDetails?.breakoutRoomId) return;
        if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
        if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
        const closesAt = typeof signal.closesAt === "string" ? signal.closesAt : undefined;
        const remaining = Math.max(0, new Date(closesAt ?? Date.now()).getTime() - Date.now());
        const closeAt = new Date(closesAt ?? Date.now()).getTime();
        const initialSeconds = Math.ceil(remaining / 1000);
        setBreakoutCountdown(initialSeconds);
        showBreakoutNotice({
          kind: "status",
          text: tBreakout("closeCountdown", { seconds: initialSeconds })
        });
        countdownIntervalRef.current = setInterval(() => {
          const seconds = Math.max(0, Math.ceil((closeAt - Date.now()) / 1000));
          setBreakoutCountdown(seconds);
          if (seconds === 0 && countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
        }, 1000);
        closeTimerRef.current = setTimeout(() => {
          void returnToMainMeeting();
        }, remaining);
      }
    },
    [
      connectionDetails?.breakoutRoomId,
      connectionDetails?.guestId,
      joinBreakoutRoom,
      returnToMainMeeting,
      showBreakoutNotice,
      tBreakout
    ]
  );

  useEffect(
    () => () => {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
      if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
      if (breakoutToastTimeoutRef.current) clearTimeout(breakoutToastTimeoutRef.current);
    },
    []
  );

  useEffect(() => {
    if (!connectionDetails?.breakoutRoomId || !breakoutSessionEndsAt) return;
    const timer = window.setInterval(() => setBreakoutTimerTick(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [connectionDetails?.breakoutRoomId, breakoutSessionEndsAt]);

  const breakoutTimerRemainingSec =
    breakoutSessionEndsAt && connectionDetails?.breakoutRoomId
      ? Math.max(0, Math.ceil((new Date(breakoutSessionEndsAt).getTime() - breakoutTimerTick) / 1000))
      : null;

  useEffect(() => {
    if (!isGuest || !connectionDetails?.breakoutRoomId || breakoutTimerRemainingSec !== 0) {
      setBreakoutExpiredPromptOpen(false);
      return;
    }
    setBreakoutExpiredPromptOpen(true);
  }, [isGuest, connectionDetails?.breakoutRoomId, breakoutTimerRemainingSec]);

  const chatIframeUrl = useMemo(
    () => (chatIframeId ? resolveChatEmbedUrl(connectionDetails, chatIframeId, locale) : null),
    [connectionDetails, chatIframeId, locale]
  );
  const chatIframeRemountKey = useMemo(() => {
    if (!chatIframeId) return null;
    const guestToken = connectionDetails?.guestToken ?? "";
    return `${chatIframeId}:${guestToken}`;
  }, [chatIframeId, connectionDetails?.guestToken]);

  // UI State & Media Controls
  const uiState = useUIState();
  const mediaControls = useMediaControls({ room, userChoices });

  const hasAutoStartedPresenting = useRef(false);
  const guestJoinMirrorKey = useRef<string | null>(null);

  useEffect(() => {
    if (startPresenting && !hasAutoStartedPresenting.current) {
      hasAutoStartedPresenting.current = true;
      mediaControls.toggleScreenShare();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startPresenting]);

  useEffect(() => {
    if (!isGuest || !guestId || !roomName || !connectionDetails?.chatId) {
      return;
    }

    const guestName = connectionDetails?.joinUserName || room.localParticipant.name || "Guest";

    // The guest session is ended by the LiveKit participant_left webhook.
    // Ending it in an effect cleanup is unsafe: React Strict Mode and normal
    // dependency changes run cleanups while the participant is still present.
    if (connectionDetails.chatEnabled) return;

    const joinKey = `${roomName}:${guestId}`;
    if (guestJoinMirrorKey.current === joinKey) return;
    guestJoinMirrorKey.current = joinKey;
    void mirrorGuestChatActivity({ roomName, guestId, guestName, action: "joined" });
  }, [
    isGuest,
    guestId,
    roomName,
    connectionDetails?.chatEnabled,
    connectionDetails?.chatId,
    connectionDetails?.joinUserName,
    room.localParticipant.name
  ]);

  // Room & Feature Hooks
  const { isReconnecting } = useRoomConnection({ room });
  const chat = useChat({
    room,
    showChat: chatIframeEnabled ? false : uiState.showChat
  });
  const reactions = useReactions({ room });
  const polls = usePolls({ room, showPolls: uiState.showPolls });
  const notes = useNotes({ room, showNotes: uiState.showNotes, liveCursorsDisabled });
  const artifactArchive = useSessionArtifactArchive({
    roomName: connectionDetails?.roomName,
    occurrenceId: connectionDetails?.odooOccurrenceId ?? connectionDetails?.odooEventId,
    breakoutRoomId: connectionDetails?.breakoutRoomId,
    notes: notes.sharedNotes,
    enabled: isModerator
  });
  const whiteboard = useWhiteboard({ room, isModerator, liveCursorsDisabled });
  const whiteboardArchiveTimer = useRef<number | null>(null);

  // The moderator is the authorized artifact writer. Archive the shared scene
  // when it changes even if the moderator has the whiteboard panel closed and
  // the edit came from another participant.
  useEffect(() => {
    if (!isModerator || !whiteboard.remoteElements.some((element) => !element.isDeleted)) return;

    if (whiteboardArchiveTimer.current) window.clearTimeout(whiteboardArchiveTimer.current);
    whiteboardArchiveTimer.current = window.setTimeout(() => {
      void whiteboard
        .createFinalSnapshot()
        .then((snapshot) => {
          if (snapshot) artifactArchive.saveWhiteboardSnapshot(snapshot);
        })
        .catch((error: unknown) => {
          Logger.warn("Meet", "Unable to archive shared whiteboard snapshot", error);
        });
      whiteboardArchiveTimer.current = null;
    }, 700);

    return () => {
      if (whiteboardArchiveTimer.current) window.clearTimeout(whiteboardArchiveTimer.current);
      whiteboardArchiveTimer.current = null;
    };
  }, [
    isModerator,
    whiteboard,
    artifactArchive,
    whiteboard.remoteElements,
    whiteboard.createFinalSnapshot,
    artifactArchive.saveWhiteboardSnapshot
  ]);

  const saveFinalArtifacts = useCallback(async () => {
    let finalWhiteboard: Blob | null = null;
    try {
      finalWhiteboard = await whiteboard.createFinalSnapshot();
    } catch (error) {
      Logger.warn("Meet", "Unable to export final whiteboard image", error);
    }
    await artifactArchive.flushFinalSave(notes.sharedNotes, finalWhiteboard);
  }, [artifactArchive, notes.sharedNotes, whiteboard]);
  const recording = useRecording({ room });
  const meetingEndState = useMeetingEndState({
    room,
    onIntentionalLeave,
    participantId: connectionDetails?.participantId,
    participantIdentity: room.localParticipant.identity,
    onBeforeMeetingEnd: saveFinalArtifacts
  });
  const muteNotification = MuteNotification({ room });
  const { participantMuteStates } = useParticipantMuteState({ room });
  useMeetEventSounds({ room });
  const [isRoomLocked, setIsRoomLocked] = useState(false);
  const joinRequestsHook = useJoinRequests({ room });
  const participantsList = useParticipantsList(room);

  // Aggregates actions (Mute, Kick, Leave, End Meeting).
  const meetHandlers = useMeetHandlers({
    room,
    meetingLink: connectionDetails?.roomName,
    isRecording: recording.isRecording,
    onStopRecording: recording.handleStopRecording,
    onBeforeMeetingEnd: saveFinalArtifacts,
    onIntentionalLeave
  });

  // Toggle room lock.
  const toggleRoomLock = useCallback(() => {
    setIsRoomLocked((prev) => !prev);
  }, []);

  /**
   * Factory for UI toggle handlers.
   * Closes the "Leave Modal" if open when switching sidebars.
   */
  const createToggleHandler = useCallback(
    (toggleFn: () => void) => () => {
      if (meetHandlers.showLeaveModal) meetHandlers.closeLeaveModal();
      toggleFn();
    },
    [meetHandlers]
  );

  const handleToggleChat = useMemo(
    () => createToggleHandler(uiState.toggleChat),
    [createToggleHandler, uiState.toggleChat]
  );
  const handleToggleParticipants = useMemo(
    () => createToggleHandler(uiState.toggleParticipants),
    [createToggleHandler, uiState.toggleParticipants]
  );
  const handleTogglePolls = useMemo(
    () => createToggleHandler(uiState.togglePolls),
    [createToggleHandler, uiState.togglePolls]
  );
  const handleToggleNotes = useMemo(
    () => createToggleHandler(uiState.toggleNotes),
    [createToggleHandler, uiState.toggleNotes]
  );
  const handleToggleSettings = useMemo(
    () => createToggleHandler(uiState.toggleSettings),
    [createToggleHandler, uiState.toggleSettings]
  );
  const handleToggleMeetingDetails = useMemo(
    () => createToggleHandler(uiState.toggleMeetingDetails),
    [createToggleHandler, uiState.toggleMeetingDetails]
  );

  /**
   * Cleans up local state before leaving.
   * Hosts explicitly close sidebars to focus on the "End Meeting" modal.
   */
  const handleLeave = useCallback(() => {
    // If we're opening the leave modal (host flow), close other sidebars for clarity
    if (isHost) {
      if (uiState.showChat) uiState.closeChat();
      if (uiState.showParticipants) uiState.closeParticipants();
      if (uiState.showPolls) uiState.closePolls();
      if (uiState.showNotes) uiState.closeNotes();
      if (uiState.showSettings) uiState.toggleSettings();
      if (uiState.showMeetingDetails) uiState.closeMeetingDetails();
    }
    meetHandlers.handleLeave();
  }, [isHost, uiState, meetHandlers]);

  const handleToggleMic = useCallback(() => {
    // Check if we are muted (so we are about to unmute)
    const isMuted = !mediaControls.isMicEnabled;
    if (isMuted && reactions.isHandRaised) {
      reactions.lowerHand();
    }
    mediaControls.toggleMic();
  }, [mediaControls, reactions]);

  const handleLowerParticipantHand = useCallback(
    (participantId: string) => {
      if (participantId === room.localParticipant.sid) {
        reactions.lowerHand();
      } else {
        reactions.requestLowerHand(participantId);
      }
    },
    [room.localParticipant.sid, reactions]
  );

  const handleHostMeetingControls = useCallback((message: Record<string, unknown>) => {
    if (typeof message.liveCursorsDisabled === "boolean") {
      setLiveCursorsDisabled(message.liveCursorsDisabled);
    }
  }, []);

  /**
   * Maps data channel events to hook handlers.
   * Memoized to prevent frequent re-binding in `useDataChannelManager`.
   */
  const dataChannelHandlers = useMemo(
    () => ({
      ...(chatIframeEnabled
        ? {}
        : {
            chat: {
              addChatMessage: chat.addChatMessage,
              incrementUnreadMessages: chat.incrementUnreadMessages
            }
          }),
      reactions: {
        handleReactionDataReceived: reactions.handleReactionDataReceived
      },
      polls: {
        handlePollDataReceived: polls.handlePollDataReceived
      },
      notes: {
        handleNotesDataReceived: notes.handleNotesDataReceived
      },
      whiteboard: {
        handleWhiteboardDataReceived: whiteboard.handleWhiteboardDataReceived
      },
      joinRequests: {
        handleJoinRequestDataReceived: joinRequestsHook.handleJoinRequestDataReceived
      },
      meetingEnd: meetingEndState,
      muteRequest: {
        handleMuteRequest: muteNotification.handleMuteRequest
      },
      breakout: {
        handleBreakoutDataReceived
      },
      hostControls: {
        handleHostMeetingControls,
        handleHostStopScreenShare: muteNotification.handleHostStopScreenShare
      }
    }),
    [
      chatIframeEnabled,
      chat.addChatMessage,
      chat.incrementUnreadMessages,
      reactions.handleReactionDataReceived,
      polls.handlePollDataReceived,
      notes.handleNotesDataReceived,
      whiteboard.handleWhiteboardDataReceived,
      joinRequestsHook.handleJoinRequestDataReceived,
      meetingEndState,
      muteNotification.handleMuteRequest,
      muteNotification.handleHostStopScreenShare,
      handleBreakoutDataReceived,
      handleHostMeetingControls
    ]
  );

  // Subscribes to LiveKit data events.
  useDataChannelManager({
    room,
    showChat: uiState.showChat,
    handlers: dataChannelHandlers
  });

  // Track unread join requests
  const [hasUnreadJoinRequests, setHasUnreadJoinRequests] = useState(false);
  const prevJoinRequestsLen = useRef(0);
  const prevPendingParticipantsLen = useRef(0);

  useEffect(() => {
    // If we have more requests than before, and sidebar is closed, mark unread
    if (joinRequestsHook.joinRequests.length > prevJoinRequestsLen.current) {
      if (!uiState.showParticipants) {
        setHasUnreadJoinRequests(true);
      }
    }
    prevJoinRequestsLen.current = joinRequestsHook.joinRequests.length;
  }, [joinRequestsHook.joinRequests.length, uiState.showParticipants]);

  useEffect(() => {
    // If pending participants list grows (e.g. background fetch), mark unread
    if (participantsList.pendingParticipants.length > prevPendingParticipantsLen.current) {
      if (!uiState.showParticipants) {
        setHasUnreadJoinRequests(true);
      }
    }
    prevPendingParticipantsLen.current = participantsList.pendingParticipants.length;
  }, [participantsList.pendingParticipants.length, uiState.showParticipants]);

  // Clear unread when sidebar opens
  useEffect(() => {
    if (uiState.showParticipants) {
      setHasUnreadJoinRequests(false);
    }
  }, [uiState.showParticipants]);

  // Clear unread if no requests left (both live and persistent)
  useEffect(() => {
    if (joinRequestsHook.joinRequests.length === 0 && participantsList.pendingParticipants.length === 0) {
      setHasUnreadJoinRequests(false);
    }
  }, [joinRequestsHook.joinRequests.length, participantsList.pendingParticipants.length]);

  /**
   * Wrappers for join request actions to clear the "unread" indication.
   * Assuming any action on the popup acknowledges the notifications.
   */
  const handleApproveJoinRequest = useCallback(
    async (request: JoinRequestWithTimer) => {
      const result = await joinRequestsHook.approveRequest(request);
      setHasUnreadJoinRequests(false);
      return result;
    },
    [joinRequestsHook]
  );

  const handleDenyJoinRequest = useCallback(
    async (request: JoinRequestWithTimer) => {
      const result = await joinRequestsHook.denyRequest(request);
      setHasUnreadJoinRequests(false);
      return result;
    },
    [joinRequestsHook]
  );

  const handleDismissJoinRequest = useCallback(
    (request: JoinRequestWithTimer) => {
      joinRequestsHook.removeRequest(request);
      setHasUnreadJoinRequests(false);
    },
    [joinRequestsHook]
  );

  return (
    <RoomContext.Provider value={room}>
      <RoomAudioRenderer />
      <MeetContent
        // Media state
        isMicEnabled={mediaControls.isMicEnabled}
        isCameraEnabled={mediaControls.isCameraEnabled}
        isScreenSharing={mediaControls.isScreenSharing}
        // UI state
        showChat={uiState.showChat}
        showParticipants={uiState.showParticipants}
        showSettings={uiState.showSettings}
        showPolls={uiState.showPolls}
        showWhiteboard={uiState.showWhiteboard}
        showNotes={uiState.showNotes}
        showMeetingDetails={uiState.showMeetingDetails}
        // Host Leave state
        showLeaveModal={meetHandlers.showLeaveModal}
        onCloseLeaveModal={meetHandlers.closeLeaveModal}
        onHostLeave={meetHandlers.handleHostLeave}
        isLeaving={meetHandlers.isLeaving}
        loadingOption={meetHandlers.loadingOption}
        participantCount={meetHandlers.participantCount}
        // Chat state
        chatMessages={chatIframeEnabled ? undefined : chat.chatMessages}
        chatInput={chatIframeEnabled ? undefined : chat.chatInput}
        chatIframeEnabled={chatIframeEnabled}
        chatIframeId={chatIframeId}
        chatIframeUrl={chatIframeUrl}
        chatIframeRemountKey={chatIframeRemountKey}
        showChatButton={showChatButton}
        // unread messages
        unreadMessages={chatIframeEnabled ? 0 : chat.unreadMessages}
        unreadPolls={polls.unreadPolls}
        unreadNotes={notes.unreadNotes}
        hasUnreadJoinRequests={hasUnreadJoinRequests}
        // Room state
        isRoomLocked={isRoomLocked}
        isHandRaised={reactions.isHandRaised}
        isRecording={recording.isRecording}
        isRecordingLoading={recording.isLoading}
        recordingError={recording.error}
        recordingStartedAt={recording.recordingStartedAt}
        isReconnecting={isReconnecting}
        // Reactions & Participants
        reactions={reactions.reactions}
        participantReactions={reactions.participantReactions}
        participantMuteStates={participantMuteStates}
        // Features
        polls={polls.polls}
        sharedNotes={notes.sharedNotes}
        remoteNotesPresence={notes.remoteNotesPresence}
        localParticipantIdentity={room.localParticipant.identity}
        localParticipantName={
          connectionDetails?.joinUserName || room.localParticipant.name || room.localParticipant.identity
        }
        remoteElements={whiteboard.remoteElements}
        whiteboardCollaborators={whiteboard.collaborators}
        whiteboardLockedForParticipants={whiteboard.lockedForParticipants}
        // Host info
        isHost={isHost}
        isModerator={isModerator}
        connectionDetails={connectionDetails!}
        // Refs
        fileInputRef={chatIframeEnabled ? undefined : chat.fileInputRef}
        // Join requests
        joinRequests={joinRequestsHook.joinRequests}
        // Callbacks - Media
        onToggleMic={handleToggleMic}
        onToggleCamera={mediaControls.toggleCamera}
        onToggleScreenShare={mediaControls.toggleScreenShare}
        // Callbacks - UI toggles
        onToggleChat={handleToggleChat}
        onToggleParticipants={handleToggleParticipants}
        onToggleSettings={handleToggleSettings}
        onTogglePolls={handleTogglePolls}
        onToggleWhiteboard={uiState.toggleWhiteboard}
        onToggleNotes={handleToggleNotes}
        onToggleMeetingDetails={handleToggleMeetingDetails}
        showBreakoutControl={(isHost || isModerator) && !connectionDetails?.breakoutRoomId}
        isInBreakout={Boolean(connectionDetails?.breakoutRoomId)}
        breakoutRoomId={connectionDetails?.breakoutRoomId ?? null}
        breakoutRoomTitle={
          connectionDetails?.breakoutRoomId
            ? connectionDetails.breakoutTitle || tBreakout("breakoutRoomFallback", { number: 1 })
            : null
        }
        breakoutThemeColor={connectionDetails?.breakoutThemeColor ?? null}
        breakoutTimerRemainingSec={breakoutTimerRemainingSec}
        breakoutCloseCountdownSec={breakoutCountdown}
        onToggleBreakouts={() => setIsBreakoutPanelOpen(true)}
        onReturnToMainMeeting={() => void returnToMainMeeting()}
        pendingBreakoutAssignment={
          connectionDetails?.breakoutRoomId || breakoutInvite || breakoutChoices ? null : pendingBreakoutAssignment
        }
        onJoinAssignedBreakout={() => {
          if (!pendingBreakoutAssignment) return;
          void joinBreakoutRoom(pendingBreakoutAssignment.breakoutRoomId, connectionDetails?.guestId);
        }}
        // Callbacks - Close handlers
        onCloseChat={uiState.closeChat}
        onCloseParticipants={uiState.closeParticipants}
        onClosePolls={uiState.closePolls}
        onCloseWhiteboard={uiState.closeWhiteboard}
        onCloseNotes={uiState.closeNotes}
        onCloseMeetingDetails={uiState.closeMeetingDetails}
        // Callbacks - Actions
        onLeave={handleLeave}
        onChatInputChange={chatIframeEnabled ? undefined : chat.setChatInput}
        onSendMessage={chatIframeEnabled ? undefined : chat.handleSendMessage}
        onFileSelect={chatIframeEnabled ? undefined : chat.handleFileSelect}
        onToggleRoomLock={toggleRoomLock}
        onToggleHandRaise={reactions.toggleHandRaise}
        onSendReaction={reactions.sendReaction}
        onCreatePoll={polls.handleCreatePoll}
        onVote={polls.handleVote}
        onClosePoll={polls.handleClosePoll}
        onUpdateNotes={notes.handleUpdateNotes}
        onPublishNotesPresence={notes.publishNotesPresence}
        onElementsChange={whiteboard.handleElementsChange}
        onWhiteboardPointerUpdate={whiteboard.handlePointerUpdate}
        onToggleWhiteboardLock={whiteboard.toggleLockForParticipants}
        onWhiteboardSnapshot={artifactArchive.saveWhiteboardSnapshot}
        onStartRecording={recording.handleStartRecording}
        onStopRecording={recording.handleStopRecording}
        // Callbacks - Moderator
        onMuteParticipant={meetHandlers.handleMuteParticipant}
        onUnmuteParticipant={meetHandlers.handleUnmuteParticipant}
        onMuteParticipantVideo={meetHandlers.handleMuteParticipantVideo}
        onUnmuteParticipantVideo={meetHandlers.handleUnmuteParticipantVideo}
        onKickParticipant={meetHandlers.handleKickParticipant}
        onMuteAllParticipants={meetHandlers.handleMuteAllParticipants}
        onStopAllParticipantVideos={meetHandlers.handleStopAllParticipantVideos}
        onStopAllScreenShares={meetHandlers.handleStopAllScreenShares}
        liveCursorsDisabled={liveCursorsDisabled}
        onLiveCursorsDisabledChange={setLiveCursorsDisabled}
        onLowerParticipantHand={handleLowerParticipantHand}
        // Callbacks - Join requests
        onApproveJoinRequest={handleApproveJoinRequest}
        onDenyJoinRequest={handleDenyJoinRequest}
        onDismissJoinRequest={handleDismissJoinRequest}
        participantsList={participantsList}
      />
      {roomName && connectionDetails && (
        <BreakoutRoomsPanel
          roomName={roomName}
          isOpen={isBreakoutPanelOpen}
          onClose={() => setIsBreakoutPanelOpen(false)}
          liveSessionEndsAt={breakoutSessionEndsAt}
          onAutoCloseDue={() => setIsBreakoutPanelOpen(true)}
          isReconnecting={isReconnecting}
          connectionDetails={connectionDetails}
          onSwitchRoom={applyBreakoutConnection}
        />
      )}
      {breakoutInvite && !connectionDetails?.breakoutRoomId && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/50 p-4">
          <section
            className="w-full max-w-sm rounded-2xl bg-white p-6 text-gray-900 shadow-2xl"
            role="dialog"
            aria-modal="true"
            aria-labelledby="breakout-invite-title"
          >
            <h2 id="breakout-invite-title" className="text-lg font-semibold text-gray-900">
              {tBreakout("inviteTitle", { room: breakoutInvite.breakoutTitle })}
            </h2>
            <p className="mt-2 text-sm text-gray-600">{tBreakout("inviteBody")}</p>
            <div className="mt-5 flex flex-wrap justify-end gap-2">
              <button
                type="button"
                className="btn-secondary min-h-10 px-4 py-2 text-sm font-medium text-gray-900"
                onClick={() => setBreakoutInvite(null)}
              >
                {tBreakout("inviteDecline")}
              </button>
              <button
                type="button"
                className="btn-primary min-h-10 px-4 py-2 text-sm font-medium"
                onClick={() => void joinBreakoutRoom(breakoutInvite.breakoutRoomId, breakoutInvite.guestId)}
              >
                {tBreakout("inviteJoin")}
              </button>
            </div>
          </section>
        </div>
      )}
      {breakoutChoices && !connectionDetails?.breakoutRoomId && !breakoutInvite && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/50 p-4">
          <section
            className="w-full max-w-sm rounded-2xl bg-white p-6 text-gray-900 shadow-2xl"
            role="dialog"
            aria-modal="true"
            aria-labelledby="breakout-choice-title"
          >
            <h2 id="breakout-choice-title" className="text-lg font-semibold text-gray-900">
              {tBreakout("chooseTitle")}
            </h2>
            <div className="mt-4 space-y-2">
              {breakoutChoices.rooms.map((choice) => (
                <button
                  key={choice.breakoutRoomId}
                  type="button"
                  className="flex w-full items-center justify-between rounded-lg border border-gray-200 px-3 py-3 text-left text-gray-900 hover:bg-gray-50"
                  onClick={() => void joinBreakoutRoom(choice.breakoutRoomId)}
                >
                  <span className="font-medium text-gray-900">{choice.name}</span>
                  <span className="text-xs text-gray-500">{choice.participantCount} people</span>
                </button>
              ))}
            </div>
            <button
              type="button"
              className="mt-4 text-sm font-medium text-gray-700 underline hover:text-gray-900"
              onClick={() => setBreakoutChoices(null)}
            >
              {tBreakout("chooseDismiss")}
            </button>
          </section>
        </div>
      )}
      {breakoutJoinError && (
        <div
          role="alert"
          className="fixed bottom-24 left-1/2 z-[120] flex max-w-md -translate-x-1/2 flex-col gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900 shadow-lg"
        >
          <p>{breakoutJoinError.message}</p>
          <div className="flex justify-end gap-2">
            <button type="button" className="underline" onClick={() => setBreakoutJoinError(null)}>
              {tBreakout("inviteDecline")}
            </button>
            <button
              type="button"
              className="rounded-lg bg-red-700 px-3 py-1.5 font-medium text-white"
              onClick={() => void joinBreakoutRoom(breakoutJoinError.breakoutRoomId)}
            >
              {tBreakout("joinRetry")}
            </button>
          </div>
        </div>
      )}
      {breakoutExpiredPromptOpen && isGuest && connectionDetails?.breakoutRoomId && (
        <div className="fixed inset-0 z-[130] flex items-center justify-center bg-black/55 p-4">
          <section
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="breakout-expired-title"
            aria-describedby="breakout-expired-description"
            className="w-full max-w-md rounded-2xl bg-white p-6 text-gray-900 shadow-2xl"
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 id="breakout-expired-title" className="text-lg font-semibold">
                  Breakout time is up
                </h2>
                <p id="breakout-expired-description" className="mt-2 text-sm text-gray-600">
                  Your breakout session has ended. Return to the main meeting to continue.
                </p>
              </div>
              <button
                type="button"
                aria-label="Close breakout time notice"
                className="rounded-md p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-900"
                onClick={() => setBreakoutExpiredPromptOpen(false)}
              >
                <X className="size-5" aria-hidden />
              </button>
            </div>
            <div className="mt-5 flex justify-end">
              <button
                type="button"
                className="btn-primary min-h-10 px-4 py-2 text-sm font-medium"
                onClick={() => void returnToMainMeeting()}
              >
                Return to main meeting
              </button>
            </div>
          </section>
        </div>
      )}
      {breakoutNotice?.kind === "status" ? (
        <div
          role="status"
          className="fixed left-1/2 top-5 z-[120] max-w-lg -translate-x-1/2 rounded-xl bg-gray-900 px-5 py-3 text-center text-white shadow-xl"
        >
          {breakoutNotice.text}
        </div>
      ) : null}
      {breakoutNotice?.kind === "broadcast" ? (
        <div
          role="status"
          aria-live="assertive"
          className="fixed bottom-24 left-1/2 z-[120] w-[min(100%-2rem,32rem)] -translate-x-1/2 rounded-2xl border border-sky-200 bg-white p-4 text-gray-900 shadow-2xl ring-1 ring-sky-100"
        >
          <div className="flex items-start gap-3">
            {breakoutNotice.senderAvatarUrl ? (
              // eslint-disable-next-line @next/next/no-img-element -- remote participant avatar URL
              <img
                src={breakoutNotice.senderAvatarUrl}
                alt=""
                className="size-12 shrink-0 rounded-full object-cover ring-2 ring-sky-100"
              />
            ) : (
              <div
                className="flex size-12 shrink-0 items-center justify-center rounded-full bg-sky-100 text-sm font-semibold text-sky-900"
                aria-hidden
              >
                {breakoutNotice.senderName.slice(0, 1).toUpperCase()}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <p className="text-xs font-semibold uppercase tracking-wide text-sky-800">
                {tBreakout("broadcastFromHost", { name: breakoutNotice.senderName })}
              </p>
              <p className="mt-1 text-base font-medium leading-snug text-gray-900">{breakoutNotice.message}</p>
            </div>
            <button
              type="button"
              className="shrink-0 rounded-lg px-2 py-1 text-sm font-medium text-gray-600 hover:bg-gray-100"
              onClick={() => setBreakoutNotice(null)}
            >
              {tBreakout("broadcastDismiss")}
            </button>
          </div>
        </div>
      ) : null}
    </RoomContext.Provider>
  );
});

export default Meet;
